/**
 * Hook to list ABR filters for a community.
 *
 * @param communityId - The community to fetch filters for
 * @returns ABR filters, loading state, and errors
 */

import { useQuery } from '@apollo/client';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import type { AbrFilter } from '@moderation/domain/AbrFilter';
import { GET_ABR_FILTERS } from '@moderation/graphql';
import { useMemo } from 'react';

interface UseAbrFiltersResult {
  /** List of ABR filters for the community */
  filters: AbrFilter[];
  /** Total number of filters */
  totalCount: number;
  /** Whether the query is loading */
  loading: boolean;
  /** Domain errors from the query */
  errors: DomainError[] | null;
  /** Refetch the filters list */
  refetch: () => void;
}

export function useAbrFilters(communityId: string): UseAbrFiltersResult {
  const { data, loading, error, refetch } = useQuery(GET_ABR_FILTERS, {
    variables: { communityId },
    skip: !communityId,
    fetchPolicy: 'cache-and-network',
  });

  const errors: DomainError[] | null = useMemo(() => {
    if (!error) return null;

    return transformErrors(error);
  }, [error]);

  const filters: AbrFilter[] = useMemo(() => data?.abrFilters?.filters ?? [], [data]);

  const totalCount = data?.abrFilters?.totalCount ?? 0;

  return {
    filters,
    totalCount,
    loading,
    errors,
    refetch,
  };
}
